<template>
  <div :class="['home', 'relative', { 'no-scroll': stage !== 'main' }]">
    <!-- Admin Icon (Top Right) – visible only if user is admin AND stage is "main" -->
    <div
      v-if="isAdmin && stage === 'main'"
      class="admin-icon"
      :class="{ openMenu: !showAdminDashboard }"
      @click="requestCloseDashboard"
      :style="{
        transform:
          isHidden && !showAdminDashboard
            ? 'translateY(-100px)'
            : 'translateY(0)',
      }"
    >
      <svg class="icon">
        <use :href="showAdminDashboard ? closeIcon : cogIcon" />
      </svg>
    </div>

    <!-- Logout Button (Top Left) – visible when user is logged in -->
    <div
      v-if="auth.currentUser && stage === 'main'"
      class="logout-button-wrapper"
      :style="{
        transform:
          isHidden && !showAdminDashboard
            ? 'translateY(-100px)'
            : 'translateY(0)',
      }"
    >
      <LogoutButton @show-dialog="showLogoutDialog = true" />
    </div>

    <!-- Confirmation Dialog (moved to root level for proper z-index stacking) -->
    <ConfirmationDialog
      v-if="stage === 'main'"
      :show="showLogoutDialog"
      message="Are you sure you want to sign out?"
      @confirm="handleLogoutConfirm"
      @cancel="showLogoutDialog = false"
      class="global-confirmation-dialog"
    />

    <!-- Password Overlay (Highest layer) -->
    <div
      v-if="stage === 'password'"
      class="password-overlay fade-in"
      :class="{ 'fade-out': isFadingOutPassword }"
    >
      <PasswordInput @authenticated="onAuthenticated" />
    </div>

    <!-- Legal Disclaimer (After authentication, before intro) -->
    <div
      v-else-if="stage === 'legal'"
      class="legal-overlay fade-in"
      :class="{ 'fade-out': isFadingOutLegal }"
    >
      <LegalDisclaimer :user="auth.currentUser" @accepted="onLegalAccepted" />
    </div>

    <!-- Intro Section (Middle layer) -->
    <section
      v-else-if="stage === 'intro'"
      class="intro-section fade-in"
      :class="{ 'fade-out': isFadingOut }"
    >
      <!-- Typewriter Intro -->
      <TypewriterText
        :typewriterFinished="typewriterFinished"
        @typingFinished="onTypingFinished"
        @introFinished="onIntroFinished"
        @skip="handleSkip"
      />
    </section>

    <!-- Main Content (Lowest layer) -->
    <div v-else class="main-content">
      <ThePathHeader class="section" />

      <PatriciaRibeiro class="section" />
      <ThePathUniverse class="section" />
      <Epilogue class="section" />

      <!-- AdminDashboard slides in if toggled -->
      <AdminDashboard
        :isOpen="showAdminDashboard"
        :closeRequestCount="closeRequestCount"
        :currentUserId="auth.currentUser ? auth.currentUser.uid : ''"
        @closeDashboard="toggleAdminDashboard"
      />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed, watch } from "vue";
import { useAuth } from "@/firebase/useAuth";
import useScrollHide from "@/composables/useScrollHide";
import { toast } from "vue3-toastify";

// Subcomponents
import ThePathUniverse from "@/components/ThePathUniverse.vue";
import ThePathHeader from "@/components/ThePathUniverse/ThePathHeader.vue";

import PatriciaRibeiro from "@/components/ThePathUniverse/PatriciaRibeiro.vue";
import Epilogue from "@/components/Epilogue.vue";

import TypewriterText from "@/components/TypewriterText.vue";
import PasswordInput from "@/components/PasswordInput.vue";
import LegalDisclaimer from "@/components/GenericComponents/LegalDisclaimer.vue";
import AdminDashboard from "@/components/GenericComponents/AdminDashboard.vue";
import LogoutButton from "@/components/GenericComponents/LogoutButton.vue";
import ConfirmationDialog from "@/components/GenericComponents/ConfirmationDialog.vue";

// Firebase auth
import { auth } from "@/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore";

export default {
  name: "HomeView",
  components: {
    ThePathUniverse,
    ThePathHeader,
    PatriciaRibeiro,
    Epilogue,
    TypewriterText,
    PasswordInput,
    LegalDisclaimer,
    AdminDashboard,
    LogoutButton,
    ConfirmationDialog,
  },
  setup() {
    // Stage management for password, intro, main
    const stage = ref("password");
    const showSkip = ref(false);
    const typewriterFinished = ref(false);
    const buttonText = ref("Skip intro");
    const isFadingOut = ref(false);
    const isFadingOutPassword = ref(false);
    const isFadingOutLegal = ref(false);
    const showAdminDashboard = ref(false);
    // Removed currentUserId

    // Keep track of how many times the user requests to close the Admin Dashboard
    const closeRequestCount = ref(0);

    // Reactive reference for the current user's Firestore document.
    const currentUserDoc = ref({ privileges: "visitor" });

    // Computed flag: true if current user's privileges are admin.
    const isAdmin = computed(() => currentUserDoc.value.privileges === "admin");

    // Icon paths
    const cogIcon = require("@/assets/iconset.svg") + "#cogwheel";
    const closeIcon = require("@/assets/iconset.svg") + "#cross";

    // For skipping the intro automatically after some time
    let autoSkipTimeout = null;

    // Called when user clicks the top-right cog/cross to close or open the Admin Dashboard
    function requestCloseDashboard() {
      closeRequestCount.value += 1;
    }

    // Show/hide the Admin Dashboard upon successful closure or direct toggling
    function toggleAdminDashboard() {
      showAdminDashboard.value = !showAdminDashboard.value;
    }

    // Called when user authenticates via PasswordInput
    async function onAuthenticated() {

      // Check if user is logged in to Firebase
      // If not, just go to intro (they need to login/register there)
      if (!auth.currentUser) {
        fadeOutPasswordThenGoIntro();
        return;
      }

      // Fetch user doc to see if they're admin and check agreement status
      await fetchCurrentUserDoc();

      // For existing users who don't have the field, assume they've already agreed
      // New users will have this field explicitly set during registration
      if (currentUserDoc.value.confidentiality_agreement_signed === undefined) {
        // Update the user document to set the field for future reference
        try {
          const db = getFirestore();
          const userRef = doc(db, "users", auth.currentUser.uid);
          await updateDoc(userRef, {
            confidentiality_agreement_signed: true,
            confidentiality_agreement_signed_at: new Date(),
          });
        } catch (error) {
        }
        fadeOutPasswordThenGoIntro();
      }
      // Check if user has already signed the confidentiality agreement
      else if (currentUserDoc.value.confidentiality_agreement_signed) {
        fadeOutPasswordThenGoIntro();
      } else {
        fadeOutPasswordThenGoLegal();
      }
    }

    function fadeOutPasswordThenGoLegal() {
      isFadingOutPassword.value = true;
      setTimeout(() => {
        isFadingOutPassword.value = false;
        stage.value = "legal";
        document.body.style.overflow = "hidden";
      }, 1000);
    }

    function fadeOutPasswordThenGoIntro() {
      isFadingOutPassword.value = true;
      setTimeout(() => {
        isFadingOutPassword.value = false;
        stage.value = "intro";
        showSkip.value = true;
        document.body.style.overflow = "hidden";
      }, 1000);
    }

    // Called when user accepts the legal disclaimer
    async function onLegalAccepted() {

      // Update user document to record acceptance of terms
      if (auth.currentUser) {
        try {
          const db = getFirestore();
          const userRef = doc(db, "users", auth.currentUser.uid);

          await updateDoc(userRef, {
            confidentiality_agreement_signed: true,
            confidentiality_agreement_signed_at: new Date(),
          });

          // Update local user doc
          currentUserDoc.value.confidentiality_agreement_signed = true;
          currentUserDoc.value.confidentiality_agreement_signed_at = new Date();

        } catch (error) {
        }
      }

      fadeOutLegalThenGoIntro();
    }

    function fadeOutLegalThenGoIntro() {
      isFadingOutLegal.value = true;
      setTimeout(() => {
        isFadingOutLegal.value = false;
        stage.value = "intro";
        showSkip.value = true;
        document.body.style.overflow = "hidden";
      }, 1000);
    }

    // Typewriter callbacks
    function onTypingFinished() {
      typewriterFinished.value = true;
      buttonText.value = "Next";
      autoSkipTimeout = setTimeout(() => {
        handleSkip();
      }, 4 * 60 * 1000); // 4 minutes
    }

    async function onIntroFinished() {
      if (!auth.currentUser) {
        toast.info(
          "Please log in or register to proceed to the next content.",
          {
            position: "bottom-center",
            autoClose: 5000,
            hideProgressBar: true,
          }
        );
        return;
      }

      // Check if user has signed NDA before proceeding
      await fetchCurrentUserDoc();

      if (!currentUserDoc.value.confidentiality_agreement_signed) {
        stage.value = "legal";
        document.body.style.overflow = "hidden";
        return;
      }

      stage.value = "main";
      showSkip.value = false;
      typewriterFinished.value = false;
      buttonText.value = "Skip intro";
      document.body.style.overflow = "auto";
      window.scrollTo(0, 0);
    }

    async function handleSkip() {
      if (autoSkipTimeout) {
        clearTimeout(autoSkipTimeout);
        autoSkipTimeout = null;
      }
      if (!auth.currentUser) {
        toast.info(
          "Please log in or register to proceed to the next content.",
          {
            position: "bottom-center",
            autoClose: 5000,
            hideProgressBar: true,
          }
        );
        return;
      }

      // Check if user has signed NDA before proceeding
      await fetchCurrentUserDoc();

      if (!currentUserDoc.value.confidentiality_agreement_signed) {
        stage.value = "legal";
        document.body.style.overflow = "hidden";
        return;
      }

      isFadingOut.value = true;
      setTimeout(() => {
        stage.value = "main";
        showSkip.value = false;
        typewriterFinished.value = false;
        buttonText.value = "Skip intro";
        isFadingOut.value = false;
        document.body.style.overflow = "auto";
        window.scrollTo(0, 0);
      }, 1000);
    }

    // Firestore doc fetch
    const db = getFirestore();

    async function fetchCurrentUserDoc() {
      if (auth.currentUser) {
        try {
          const docRef = doc(db, "users", auth.currentUser.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            currentUserDoc.value = docSnap.data();
          } else {
            currentUserDoc.value = {
              privileges: "visitor",
              confidentiality_agreement_signed: false,
            };
          }
        } catch (error) {
        }
      }
    }

    let unsubAuthState = null;

    onMounted(() => {
      unsubAuthState = onAuthStateChanged(auth, async (user) => {
        if (user) {
          await fetchCurrentUserDoc();
          if (stage.value === "password") {
            // For existing users who don't have the field, assume they've already agreed
            if (
              currentUserDoc.value.confidentiality_agreement_signed ===
              undefined
            ) {
              // Update the user document to set the field for future reference
              try {
                const db = getFirestore();
                const userRef = doc(db, "users", auth.currentUser.uid);
                await updateDoc(userRef, {
                  confidentiality_agreement_signed: true,
                  confidentiality_agreement_signed_at: new Date(),
                });
              } catch (error) {
              }
              fadeOutPasswordThenGoIntro();
            }
            // Check if user has already signed the confidentiality agreement
            else if (currentUserDoc.value.confidentiality_agreement_signed) {
              fadeOutPasswordThenGoIntro();
            } else {
              fadeOutPasswordThenGoLegal();
            }
          }
        } else {
        }
      });

      window.scrollTo(0, 0);
      // Ensure body class reflects initial stage
      if (stage.value !== "main") {
        document.body.classList.add("overlay-active");
      } else {
        document.body.classList.remove("overlay-active");
      }
    });

    // Watch stage and toggle a body class so global footer can be hidden
    watch(stage, (newStage) => {
      if (newStage !== "main") {
        document.body.classList.add("overlay-active");
      } else {
        document.body.classList.remove("overlay-active");
      }
    });

    onBeforeUnmount(() => {
      if (autoSkipTimeout) {
        clearTimeout(autoSkipTimeout);
      }
      if (unsubAuthState) {
        unsubAuthState();
      }
      // Clean up body class on unmount
      document.body.classList.remove("overlay-active");
    });

    const { isHidden } = useScrollHide();
    const showLogoutDialog = ref(false);
    const { logout } = useAuth();

    const handleLogoutConfirm = async () => {
      showLogoutDialog.value = false;
      try {
        await logout();
        // After successful logout, change the stage
        stage.value = "password";
        // Close admin dashboard on logout
        if (showAdminDashboard.value) {
          showAdminDashboard.value = false;
        }
      } catch (error) {
        // Handle error if needed
      }
    };

    // Watch auth.currentUser and close admin dashboard when user logs out
    watch(
      () => auth.currentUser,
      (newUser) => {
        if (!newUser && showAdminDashboard.value) {
          showAdminDashboard.value = false;
        }
      }
    );

    return {
      showLogoutDialog,
      handleLogoutConfirm,
      stage,
      showSkip,
      typewriterFinished,
      buttonText,
      isFadingOut,
      isFadingOutPassword,
      isFadingOutLegal,
      showAdminDashboard,
      closeRequestCount,
      auth,
      requestCloseDashboard,
      toggleAdminDashboard,
      onAuthenticated,
      onLegalAccepted,
      onTypingFinished,
      onIntroFinished,
      handleSkip,
      currentUserDoc,
      isAdmin,
      cogIcon,
      closeIcon,
      isHidden,
    };
  },
};
</script>

<style scoped>
.logout-button-wrapper {
  position: fixed;
  left: 0;
  z-index: 5;
  transition: transform 0.3s ease;
}

.home {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Top right icons */
.admin-icon {
  position: fixed;
  top: var(--space);
  right: var(--smallSpace);
  cursor: pointer;
  z-index: 7;
  transition: transform 0.3s ease;
}

/* 
  Only apply openMenu styles 
  when the cogwheel is showing i.e. (!showAdminDashboard) 
*/
.admin-icon.openMenu {
  background-color: var(--bg-main-dark);
  padding: 3px 3px 0 3px;
  border-radius: 50%;
}

.admin-icon:hover {
  transform: scale(1.1);
}

/* Password overlay */
.password-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  z-index: 5;
  opacity: 1;
  transition: opacity 1s ease;
}
.password-overlay.fade-out {
  opacity: 0 !important;
  filter: blur(4px);
  transition: opacity 1s ease, filter 1s ease;
}

/* Legal disclaimer overlay */
.legal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5;
  opacity: 1;
  transition: opacity 1s ease;
}
.legal-overlay.fade-out {
  opacity: 0 !important;
  filter: blur(4px);
  transition: opacity 1s ease, filter 1s ease;
}

/* Intro section */
.intro-section {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: fixed;
  z-index: 3;
  top: 0;
  left: 0;
  width: 100%;
  opacity: 1;
  transition: opacity 1s ease, filter 1s ease;
}
.fade-out {
  opacity: 0 !important;
  filter: blur(4px);
  transition: opacity 1s ease, filter 1s ease;
}

/* Main content */
.main-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-snap-type: y mandatory;
  scroll-behaviour: smooth;
}
.main-content .section {
  scroll-snap-align: start;
  position: relative;
  /* overflow-y: visible; */
}

/* Bottom section (Skip button) */
.bottom-section {
  position: fixed;
  bottom: var(--clickableHeight);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  width: 100%;
}

/* Global confirmation dialog - ensures it's above everything */
.global-confirmation-dialog {
  position: fixed;
  isolation: isolate;
  z-index: 1000; /* Higher than any other element */
}
</style>
